/**
 * RevealPanel — Sprint 1
 * Shows a POI's authored "reveal" layer (a quote, a quiet detail, a note), but
 * ONLY once the traveller has actually been near it — i.e. its slug is in the
 * trip's revealedPois. The reveal is the reward; it appears silently.
 *
 * Tolerates missing content with elegance: if the POI has no `reveal` object, or
 * has not been revealed yet, this renders nothing. Érico authors the `reveal`
 * fields directly on the POI object (see src/utils/reveal.js for the rules).
 */
import { useTranslation } from 'react-i18next';
import { poiSlug } from '../utils/reveal.js';
import Storage from '../utils/storageES.js';
import theme from '../styles/theme.js';

export default function RevealPanel({ poi }) {
  const { t } = useTranslation('common');

  if (!poi || !poi.reveal || !poi.name) return null;

  let revealed = false;
  try {
    const ids = Storage.getTripData().revealedPois || [];
    revealed = ids.includes(poiSlug(poi.name));
  } catch { revealed = false; }
  if (!revealed) return null;

  const { quote, secret, note } = poi.reveal || {};
  if (!quote && !secret && !note) return null;

  return (
    <div style={styles.wrap}>
      {quote && <div style={styles.quote}>“{quote}”</div>}
      {secret && (
        <div style={styles.row}>
          <span style={styles.label}>{t('reveal.revealSecret')}</span>
          <span style={styles.text}>{secret}</span>
        </div>
      )}
      {note && (
        <div style={styles.row}>
          <span style={styles.label}>{t('reveal.revealNote')}</span>
          <span style={styles.text}>{note}</span>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    marginTop: 12,
    padding: '12px 14px',
    background: theme.cream,
    borderLeft: `3px solid ${theme.terracotta}`,
    borderRadius: 8,
  },
  quote: {
    fontStyle: 'italic',
    fontSize: 15,
    lineHeight: 1.5,
    color: theme.brownDark,
    marginBottom: 8,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginTop: 6,
  },
  label: {
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.terracottaDark,
    fontWeight: 600,
  },
  text: {
    fontSize: 14,
    lineHeight: 1.45,
    color: theme.brownDark,
  },
};
